import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight, Phone, Star } from "lucide-react";
import { CONTACT } from "../data/site";
import { track, trackLeadConversion } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQUEST_TIMEOUT_MS = 20000;

const empty = {
  full_name: "", company: "", job_title: "", email: "", phone: "", message: "",
};

// Simplified, low-friction lead form for the Google Ads landing page ONLY.
// Posts to the same /api/leads endpoint and fires the same conversion tracking
// as the full contact form — no backend or tracking changes.
export const LandingLeadForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const convertedRef = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.company || !form.job_title || !form.email || !form.phone || !form.message) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      const source = typeof window !== "undefined" ? `Contact form (${window.location.pathname})` : "Contact form";
      const fullName = form.full_name.trim();
      const [first, ...rest] = fullName.split(/\s+/);
      const payload = {
        name: fullName,
        first_name: first || "",
        last_name: rest.join(" "),
        company: form.company,
        job_title: form.job_title,
        email: form.email,
        phone: form.phone,
        message: form.message,
        source,
      };
      await axios.post(`${API}/leads`, payload, { timeout: REQUEST_TIMEOUT_MS });
      // Backend confirmed the lead was saved — fire conversions exactly once.
      track("generate_lead", {});
      if (!convertedRef.current) {
        convertedRef.current = true;
        trackLeadConversion();
      }
      setDone(true);
      setForm(empty);
      toast.success("Request received — we'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="form-success" className="text-center py-10 px-6">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald/10 flex items-center justify-center mb-5">
          <CheckCircle2 className="h-7 w-7 text-emerald" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-charcoal mb-2">Thank you.</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          Your request has been received. A Zentryx specialist will reach out within one business day.
        </p>
        <a href={CONTACT.phoneHref} data-testid="form-success-call"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold transition-colors">
          <Phone className="h-4 w-4" /> Prefer to talk now? Call {CONTACT.phone}
        </a>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition";
  const label = "block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-1.5";

  return (
    <form data-testid="assessment-form" onSubmit={submit} className="space-y-5">
      <div>
        <label className={label}>Full name *</label>
        <input data-testid="form-full-name" className={inputCls} value={form.full_name} onChange={set("full_name")} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={label}>Company *</label><input data-testid="form-company" className={inputCls} value={form.company} onChange={set("company")} /></div>
        <div><label className={label}>Job title *</label><input data-testid="form-job-title" className={inputCls} value={form.job_title} onChange={set("job_title")} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={label}>Company email *</label><input data-testid="form-email" type="email" className={inputCls} value={form.email} onChange={set("email")} /></div>
        <div><label className={label}>Phone *</label><input data-testid="form-phone" className={inputCls} value={form.phone} onChange={set("phone")} /></div>
      </div>
      <div>
        <label className={label}>Message *</label>
        <textarea data-testid="form-message" rows={4} className={inputCls + " resize-none"} value={form.message} onChange={set("message")} placeholder="Tell us about your current IT challenges..." />
      </div>

      <button data-testid="form-submit-button" type="submit" disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald hover:bg-emerald-dark disabled:opacity-70 text-white font-semibold py-3.5 transition-colors">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {loading ? "Sending..." : "Talk to an IT Expert"}
        {!loading ? <ArrowRight className="h-4 w-4" /> : null}
      </button>
      <div data-testid="form-trust-line" className="text-center space-y-1">
        <p className="flex items-center justify-center gap-1.5 text-sm font-medium text-charcoal">
          <span className="flex items-center gap-0.5 text-emerald">
            {[0, 1, 2, 3, 4].map((i) => <Star key={`trust-star-${i}`} className="h-3.5 w-3.5 fill-emerald" />)}
          </span>
          Rated 4.9 on Google
        </p>
        <p className="text-xs text-slate-400">We'll contact you within one business day.</p>
      </div>
    </form>
  );
};
